import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

// ============================================================================
// Request/Response Interfaces
// ============================================================================

export interface CreateExportJobRequest {
  /** Module name for export (e.g., 'client', 'client_invoice') */
  module: string;
  /** Export format: excel or csv */
  export_type: 'excel' | 'csv';
  /** Optional filters to apply (same as list endpoint filters) */
  filters?: Record<string, any>;
  /** Optional list of column keys to export */
  selected_columns?: string[];
}

export interface ExportJobResponse {
  id: number;
  module: string;
  export_type: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  file_name?: string;
  file_size_bytes?: number;
  total_rows?: number;
  progress: number;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  expires_at?: string;
  error_message?: string;
}

export interface ExportJobListResponse {
  items: ExportJobResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface DownloadUrlResponse {
  download_url: string;
  expires_at: string;
}

export interface ApiResponse<T> {
  status: string;
  message: string;
  data: T;
}

export interface ExportJobFilters {
  module?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

// ============================================================================
// Export API Service
// ============================================================================

/**
 * Service for managing export jobs.
 * Provides methods to create, list, retrieve, and download export jobs.
 */
@Injectable({
  providedIn: 'root'
})
export class ExportApiService {
  private readonly API_BASE_URL = environment.accountingUrl;

  constructor(private http: HttpClient) {}

  /**
   * Create a new export job.
   * The job will be processed in the background.
   * 
   * @param request Export job configuration
   * @returns Observable with created job details
   */
  createExportJob(request: CreateExportJobRequest): Observable<ApiResponse<ExportJobResponse>> {
    return this.http.post<ApiResponse<ExportJobResponse>>(
      `${this.API_BASE_URL}/api/v1/exports`,
      request,
      { withCredentials: true }
    );
  }

  /**
   * List export jobs for the current user.
   * 
   * @param filters Optional filters for module, status, pagination
   * @returns Observable with paginated list of jobs
   */
  listExportJobs(filters?: ExportJobFilters): Observable<ApiResponse<ExportJobListResponse>> {
    let params = new HttpParams();
    
    if (filters) {
      if (filters.module) params = params.set('module', filters.module);
      if (filters.status) params = params.set('status', filters.status);
      if (filters.limit) params = params.set('limit', filters.limit.toString());
      if (filters.offset !== undefined) params = params.set('offset', filters.offset.toString());
    }

    return this.http.get<ApiResponse<ExportJobListResponse>>(
      `${this.API_BASE_URL}/api/v1/exports`,
      { params, withCredentials: true }
    );
  }

  /**
   * Get a single export job by ID.
   * 
   * @param jobId Export job ID
   * @returns Observable with job details
   */
  getExportJob(jobId: number | string): Observable<ApiResponse<ExportJobResponse>> {
    return this.http.get<ApiResponse<ExportJobResponse>>(
      `${this.API_BASE_URL}/api/v1/exports/${jobId}`,
      { withCredentials: true }
    );
  }

  /**
   * Get a presigned download URL for a completed export.
   * The URL is temporary and expires after a configured duration.
   * 
   * @param jobId Export job ID
   * @returns Observable with download URL and expiration
   */
  getDownloadUrl(jobId: number | string): Observable<ApiResponse<DownloadUrlResponse>> {
    return this.http.get<ApiResponse<DownloadUrlResponse>>(
      `${this.API_BASE_URL}/api/v1/exports/${jobId}/download`,
      { withCredentials: true }
    );
  }

  /**
   * Cancel or delete an export job.
   * - Pending jobs will be cancelled
   * - Completed/failed jobs will be soft-deleted
   * - Processing jobs cannot be cancelled
   * 
   * @param jobId Export job ID
   * @returns Observable with success/error response
   */
  deleteExportJob(jobId: number | string): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(
      `${this.API_BASE_URL}/api/v1/exports/${jobId}`,
      { withCredentials: true }
    );
  }

  /**
   * Download an export file directly.
   * First fetches the presigned URL, then triggers browser download.
   * The backend generates URLs with Content-Disposition: attachment to force download.
   * 
   * @param jobId Export job ID
   * @param fileName Optional custom filename for download
   */
  async downloadExport(jobId: number | string, fileName?: string): Promise<void> {
    const response = await this.getDownloadUrl(jobId).toPromise();
    
    if (response?.data?.download_url) {
      // Simply navigate to the presigned URL - the S3 response includes
      // Content-Disposition: attachment header which forces browser download
      window.location.href = response.data.download_url;
    }
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  /**
   * Get human-readable status label
   */
  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      'pending': 'Pending',
      'processing': 'Processing',
      'completed': 'Completed',
      'failed': 'Failed',
      'cancelled': 'Cancelled'
    };
    return labels[status] || status;
  }

  /**
   * Get status badge severity for PrimeNG
   */
  getStatusSeverity(status: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    const severities: Record<string, 'success' | 'info' | 'warn' | 'danger' | 'secondary'> = {
      'pending': 'secondary',
      'processing': 'info',
      'completed': 'success',
      'failed': 'danger',
      'cancelled': 'warn'
    };
    return severities[status] || 'secondary';
  }

  /**
   * Format file size to human-readable string
   */
  formatFileSize(bytes?: number): string {
    if (!bytes || bytes === 0) return '-';
    
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  }

  /**
   * Get human-readable module name
   */
  getModuleLabel(module: string): string {
    const labels: Record<string, string> = {
      'client': 'Clients',
      'client_invoice': 'Client Invoices',
      'vendor': 'Vendors',
      'vendor_invoice': 'Vendor Invoices',
      'item': 'Items',
      'journal_voucher': 'Journal Vouchers',
      'asset_request': 'Asset Requests'
    };
    return labels[module] || module.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }
}
